<script setup>

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="customer-stories section-mb">
  <div class="container">
    <div v-if="block.headingAdvanced?.html"
      v-html="block.headingAdvanced?.html"
      class="customer-stories__heading"></div>

    <div class="customer-stories__grid">
      <!-- An empty touchstart listener lets iOS Safari apply :active, so the tap state works -->
      <component
        v-for="story in block.cards"
        :key="story.id"
        :is="story.linkField?.url ? 'a' : 'div'"
        :href="story.linkField?.url || undefined"
        class="customer-stories__card"
        @touchstart.passive="() => {}">

        <div class="customer-stories__card-header">
          <div v-if="story.iconField?.length"
            class="customer-stories__card-icon">
            <GlobalsImage
              :image="story.iconField"
              :aria-hidden="true"
            />
          </div>
          <div v-if="story.badge"
            class="customer-stories__card-badge">
            {{ story.badge }}
          </div>
        </div>

        <div class="customer-stories__card-content">
          <p class="customer-stories__card-name">{{ story.customerName }}</p>
          <p class="customer-stories__card-text">{{ story.text }}</p>
        </div>

        <div class="customer-stories__card-image">
          <GlobalsImage
            :landscape="story.imageDesktop"
            :portrait="story.imageMobile"
            :max-width="1600"
            :ariaHidden="true"
          />
        </div>
      </component>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.customer-stories__heading {
  margin-bottom: 48px;
  text-align: center;
}

.customer-stories__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  max-width: 560px;
  margin: 0 auto;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    max-width: none;
  }
}

/* Card frame: stays put on hover, only the image inside scales */
.customer-stories__card {
  position: relative;
  display: flex;
  flex-direction: column;
  aspect-ratio: 344 / 370;
  padding: 24px 16px 16px;
  overflow: hidden;
  border-radius: 16px;
  background-color: #f7f5f0;
  color: #fff;
  isolation: isolate;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  &[href] {
    cursor: pointer;
  }

  @media (min-width: 1024px) {
    aspect-ratio: 501 / 582;
    padding: 24px;
  }

  @media (min-width: 1440px) {
    padding: 32px;
  }
}

.customer-stories__card-image {
  position: absolute;
  inset: 0;
  z-index: -1;
  transform-origin: center;
  transition: transform .3s cubic-bezier(.4, 0, .2, 1);
}

/* Darkening from the design, on the image so it scales with it */
.customer-stories__card-image::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg,
    rgba(0, 0, 0, .3) 0%,
    rgba(0, 0, 0, 0) 22%,
    rgba(0, 0, 0, 0) 62%,
    rgba(0, 0, 0, .2) 92%);
  pointer-events: none;
}

.customer-stories__card-image :deep(picture),
.customer-stories__card-image :deep(img) {
  display: block;
  width: 100%;
  height: 100%;
}

.customer-stories__card-image :deep(img) {
  object-fit: cover;
  object-position: center;
}

/* Hover on pointer devices only; touch devices get the same state on tap */
@media (hover: hover) {
  .customer-stories__card:hover .customer-stories__card-image {
    transform: scale(1.03);
  }
}

.customer-stories__card:active .customer-stories__card-image {
  transform: scale(1.03);
}

@media (prefers-reduced-motion: reduce) {
  .customer-stories__card-image {
    transition: none;
  }
}

/* Header: logo + badge */
.customer-stories__card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 0 12px;

  @media (min-width: 1024px) {
    align-items: flex-start;
    padding: 0;
  }
}

.customer-stories__card-icon {
  flex: 0 1 135px;
  min-width: 0;
  height: 45px;

  @media (min-width: 1440px) {
    flex-basis: 205px;
    height: 50px;
  }
}

.customer-stories__card-icon :deep(picture),
.customer-stories__card-icon :deep(img) {
  display: block;
  width: 100%;
  height: 100%;
}

.customer-stories__card-icon :deep(img) {
  object-fit: contain;
  object-position: left center;
}

.customer-stories__card-badge {
  flex: none;
  padding: 5px 11px;
  border: 1.4px solid #fff;
  border-radius: 8.4px;
  font-family: var(--font-family-primary);
  font-size: 12.6px;
  font-weight: 500;
  line-height: 17px;
  letter-spacing: -.02em;
  white-space: nowrap;

  @media (min-width: 1440px) {
    padding: 7px 16px;
    border-width: 1px;
    border-radius: 12px;
    font-size: 18px;
    line-height: 25px;
  }
}

/* Message bubble */
.customer-stories__card-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: auto;
  padding: 15px 17px 15px 14px;
  border-radius: 16px;
  background: rgba(37, 37, 37, .2);
  -webkit-backdrop-filter: blur(7.5px);
  backdrop-filter: blur(7.5px);

  @media (min-width: 1440px) {
    gap: 5px;
    padding: 25px 29px 25px 24px;
    -webkit-backdrop-filter: blur(12.5px);
    backdrop-filter: blur(12.5px);
  }
}

.customer-stories__card-content p {
  margin: 0;
  color: #fff;
  font-family: var(--font-family-primary);
  font-weight: 500;
  letter-spacing: -.02em;
}

.customer-stories__card-name {
  font-size: 14px;
  line-height: 19px;

  @media (min-width: 1440px) {
    font-size: 18px;
    line-height: 25px;
  }
}

.customer-stories__card-text {
  font-size: 16px;
  line-height: 22px;

  @media (min-width: 1440px) {
    font-size: 20px;
    line-height: 27px;
  }
}

</style>
